/**
 * TA v4.3 — Phase runner.
 *
 * Renders all questions for the active phase. Handles:
 *   - server-side timer countdown (ticks against `deadline_at` from /state)
 *   - auto-save on response change (debounced 1.5s)
 *   - submit phase → server flips state.locked & advances active_phase
 *
 * Phase-specific behavior (passed via `phase` prop):
 *   1 — Rapid Fire: linear question list
 *   2 — Applied Warfare: each question's response_fields are chained;
 *       field[i] is locked until field[i-1] has non-empty content
 *   3 — Judgment Under Fire: detect "write the email / message" fields and
 *       render them with EMAIL DRAFT badge + soft min-words hint
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Send, AlertTriangle, Clock, Save } from "lucide-react";
import { resolveBackendBase } from "../../lib/api";
import {
    primaryLanguageFromMeta,
    secondsUntil,
    fmtClock,
    isCommunicationField,
} from "./taHelpers";
import TAScenarioRenderer from "./TAScenarioRenderer";
import TAResponseField from "./TAResponseField";

const API_URL = `${resolveBackendBase()}/api/v2`;
const SECTION_NAMES = {
    1: { label: "Rapid Fire", phase_id: "ta_phase1" },
    2: { label: "Applied Warfare", phase_id: "ta_phase2" },
    3: { label: "Judgment Under Fire", phase_id: "ta_phase3" },
};

export default function TAPhaseRunner({ phase, state, onPhaseAdvanced }) {
    const template = state.template?.content;
    const section = (template?.content || []).find((s) => s.id === SECTION_NAMES[phase].phase_id);
    const fallbackLang = useMemo(() => primaryLanguageFromMeta(state.template), [state.template]);
    const phaseState = state.ta_phase_state?.[`phase${phase}`] || {};
    const items = section?.items || [];

    const [responses, setResponses] = useState(() => state.saved_responses || {});
    const telemetryRef = useRef({});  // { [response_field_id]: telemetry }
    const [savingState, setSavingState] = useState("idle"); // idle | saving | saved | error
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Live clock — second-level tick
    const [secsLeft, setSecsLeft] = useState(() => secondsUntil(phaseState.deadline_at));
    useEffect(() => {
        const id = setInterval(() => setSecsLeft(secondsUntil(phaseState.deadline_at)), 1000);
        return () => clearInterval(id);
    }, [phaseState.deadline_at]);

    const warnAt = section?.timer?.warning_at_seconds || 120;
    const urgent = secsLeft <= warnAt;
    const expired = secsLeft <= 0;

    // Debounced auto-save
    const lastChangeAt = useRef(Date.now());
    useEffect(() => {
        if (!Object.keys(telemetryRef.current).length) return;
        if (phaseState.locked) return;
        const t = setTimeout(async () => {
            const payload = Object.values(telemetryRef.current);
            setSavingState("saving");
            try {
                await axios.post(`${API_URL}/assessment/ta/${state.token}/phase/${phase}/save`, { telemetry: payload });
                setSavingState("saved");
                setTimeout(() => setSavingState("idle"), 1200);
            } catch {
                setSavingState("error");
            }
        }, 1500);
        return () => clearTimeout(t);
    }, [responses, phaseState.locked, phase, state.token]);

    // Handle response change
    const handleFieldChange = useCallback((field, text, tele) => {
        setResponses((r) => ({ ...r, [field.id]: text }));
        telemetryRef.current[field.id] = tele;
        lastChangeAt.current = Date.now();
    }, []);

    const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const payload = Object.values(telemetryRef.current);
            const r = await axios.post(`${API_URL}/assessment/ta/${state.token}/phase/${phase}/submit`, { telemetry: payload });
            onPhaseAdvanced && onPhaseAdvanced(r.data);
        } catch (e) {
            setSubmitError(e.response?.data?.detail || "Submission failed. Please retry.");
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid={`ta-phase-${phase}`}>
            {/* Sticky header with clock + saving indicator */}
            <header className="border-b border-border/40 bg-card sticky top-0 z-20">
                <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] font-mono text-gray-500">Phase {phase} of 3</p>
                        <h1 className="text-sm font-display font-bold text-white">{SECTION_NAMES[phase].label}</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        {savingState === "saving" && <span className="text-[11px] text-gray-500 flex items-center gap-1 font-mono"><Loader2 className="w-3 h-3 animate-spin" />Saving</span>}
                        {savingState === "saved" && <span className="text-[11px] text-emerald-300 flex items-center gap-1 font-mono"><Save className="w-3 h-3" />Saved</span>}
                        {savingState === "error" && <span className="text-[11px] text-red-300 font-mono">Save failed</span>}
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 border ${expired ? "border-red-500/60 text-red-300" : urgent ? "border-amber-500/60 text-amber-200" : "border-border/60 text-gray-300"}`} data-testid={`ta-clock-phase-${phase}`}>
                            <Clock className="w-3.5 h-3.5" />
                            <span className="text-sm font-mono tabular-nums">{fmtClock(secsLeft)}</span>
                        </div>
                    </div>
                </div>
                {/* Timer bar */}
                <div className="h-0.5 bg-border/40">
                    <div
                        className={`h-full transition-all duration-700 ${expired ? "bg-red-400" : urgent ? "bg-amber-300" : "bg-white"}`}
                        style={{ width: `${Math.min(100, Math.max(0, (secsLeft / Math.max(1, (phaseState.duration_seconds || 720))) * 100))}%` }}
                    />
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-8">
                <p className="text-xs text-gray-500 leading-relaxed mb-8">
                    {phase === 1 && "Answer each question briefly. Some contain deliberate errors — call them out where you see them."}
                    {phase === 2 && "Each question is multi-part. Answer Part A first — Part B unlocks once you commit. Once you move on, earlier parts become read-only."}
                    {phase === 3 && "Open-ended judgment. Where a question asks you to write a message, write what you'd actually send — not a paraphrase."}
                </p>

                {expired && !phaseState.locked && (
                    <div className="border border-red-500/60 bg-red-500/5 px-4 py-3 mb-6 flex items-start gap-3" data-testid={`ta-expired-banner-phase-${phase}`}>
                        <AlertTriangle className="w-4 h-4 text-red-300 mt-0.5" />
                        <div>
                            <p className="text-xs text-red-200 font-semibold">Time is up for this phase.</p>
                            <p className="text-[11px] text-red-200/70">Submit now to capture whatever you've written. Anything entered after this point is recorded as late.</p>
                        </div>
                    </div>
                )}

                <div className="space-y-10">
                    {items.map((q, qIdx) => (
                        <QuestionBlock
                            key={q.id}
                            q={q}
                            phase={phase}
                            qIdx={qIdx}
                            responses={responses}
                            telemetryRef={telemetryRef}
                            onFieldChange={handleFieldChange}
                            fallbackLang={fallbackLang}
                            locked={phaseState.locked || submitting}
                        />
                    ))}
                </div>

                {submitError && (
                    <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 my-6">
                        <p className="text-xs text-red-300" data-testid={`ta-submit-error-phase-${phase}`}>{submitError}</p>
                    </div>
                )}

                <div className="sticky bottom-4 mt-12 bg-card border border-border/40 px-5 py-4 flex items-center justify-between">
                    <p className="text-[11px] text-gray-500 leading-relaxed max-w-md">
                        Submitting locks Phase {phase}. You cannot reopen it. The clock keeps running on the server until you submit.
                    </p>
                    <button
                        onClick={submit}
                        disabled={submitting || phaseState.locked}
                        className="px-5 py-2.5 bg-white text-black text-sm font-semibold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-wait flex items-center gap-2"
                        data-testid={`ta-submit-phase-${phase}-btn`}
                    >
                        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting…</> : <><Send className="w-4 h-4" />Submit Phase {phase}</>}
                    </button>
                </div>
            </main>
        </div>
    );
}


function QuestionBlock({ q, phase, qIdx, responses, telemetryRef, onFieldChange, fallbackLang, locked }) {
    // Phase 2 chained behavior: a field becomes editable only when the previous one
    // has non-empty content. We never DISABLE editing of an already-typed field within
    // the same in-flight phase — chained read-only kicks in once the phase is submitted,
    // and the backend enforces it on /save.
    const isPhase2 = phase === 2;
    const fields = q.response_fields || [];

    // Phase 3 communication detection
    const phase3HasComms = phase === 3;

    return (
        <article className="bg-card border border-border/40 p-5" data-testid={`ta-question-${q.id}`}>
            <div className="flex items-baseline justify-between mb-3">
                <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-gray-500">Question {q.question_number || qIdx + 1}</p>
                {q.title?.includes("DELIBERATE ERROR") && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 text-amber-200 font-mono uppercase tracking-wider" data-testid={`ta-deliberate-error-tag-${q.id}`}>Deliberate error</span>
                )}
            </div>
            <h3 className="text-base font-display font-bold text-white mb-3 leading-snug">{q.title}</h3>

            <div className="mb-4">
                <TAScenarioRenderer scenario={q.scenario} fallbackLanguage={fallbackLang} />
            </div>

            {q.primary_question && (
                <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-1.5">Primary question</p>
                    <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">{q.primary_question}</p>
                </div>
            )}
            {q.follow_up && (
                <div className="mb-5">
                    <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-1.5">Follow up</p>
                    <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{q.follow_up}</p>
                </div>
            )}

            <div className="space-y-5 mt-5 pt-5 border-t border-border/30">
                {fields.map((f, fi) => {
                    // Phase 2 chained: field[i] locked until field[i-1] has content.
                    // Once a later field has content, this field becomes read-only & visually de-emphasised
                    // so the candidate can reference it but won't be tempted to revise.
                    // (Server-side enforcement happens at /save & /submit boundaries.)
                    let isLocked = locked;
                    let chainedReadOnly = false;
                    if (isPhase2) {
                        if (fi > 0) {
                            const prev = fields[fi - 1];
                            const prevText = responses[prev.id];
                            if (!prevText || prevText.trim().length === 0) {
                                return (
                                    <LockedFieldPlaceholder
                                        key={f.id}
                                        field={f}
                                        prevLabel={prev.label}
                                    />
                                );
                            }
                        }
                        // any later field has content → this one is read-only-styled
                        const laterHasContent = fields.slice(fi + 1).some((nf) => {
                            const v = responses[nf.id];
                            return v && v.trim().length > 0;
                        });
                        if (laterHasContent) {
                            chainedReadOnly = true;
                        }
                    }

                    // Phase 3 EMAIL DRAFT detection
                    let headerHint = null, labelOverride = null, minWordsHint = null;
                    if (phase3HasComms && isCommunicationField(f, q)) {
                        headerHint = "Email draft";
                        minWordsHint = 50;  // soft hint, not blocking
                    }

                    const lang = (f.type === "code") ? fallbackLang : "plaintext";
                    return (
                        <TAResponseField
                            key={f.id}
                            field={f}
                            itemId={q.id}
                            value={responses[f.id] || ""}
                            initialTelemetry={telemetryRef.current[f.id]}
                            onChange={(text, tele) => onFieldChange(f, text, tele)}
                            readOnly={isLocked || chainedReadOnly}
                            language={lang}
                            labelOverride={labelOverride}
                            headerHint={headerHint}
                            minWordsHint={minWordsHint}
                        />
                    );
                })}
            </div>
        </article>
    );
}


function LockedFieldPlaceholder({ field, prevLabel }) {
    return (
        <div className="border border-dashed border-border/40 bg-[#0a0a0a] px-4 py-3 flex items-center justify-between" data-testid={`ta-locked-field-${field.id}`}>
            <div>
                <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500">{field.label}</p>
                <p className="text-[11px] text-gray-600 mt-0.5">Locked — finish "{prevLabel}" first</p>
            </div>
            <span className="text-[10px] font-mono text-gray-600 uppercase tracking-wider">chained</span>
        </div>
    );
}
